import _ from 'lodash';
import * as Actions from 'redux/actions/index';

/**
 * Is this roster row one of the identities the invite just revoked?
 *
 * A person holds one role per course, so inviting them in one role strips the
 * other: /api/courses/inviteTeachers deletes their enrolment and cached
 * progress, /api/courses/inviteStudents pulls them out of teacherUsernames and
 * teacherIds. The roster has to drop the same rows.
 *
 * It used to try `emails.includes(row.email)`, which failed twice over. The
 * array holds invite OBJECTS, not strings, so it never matched anything. And
 * even fixed it could not work: extractCourseUsersWithMeta runs every email
 * through maskEmail, so a row reads `jte******@school.org` unless the viewer is
 * an AverPoint staff admin - meaning a naive fix passes when staff test it and
 * silently does nothing for the teachers who use the screen.
 *
 * So the server now reports exactly who it removed and we match on that:
 *
 *   usernames  enrolled rows, which carry an unmasked `username`
 *   userIds    pending-invite rows for someone WITH an account, whose `_id` is
 *              the user id (the user is spread over the invite)
 *   inviteIds  pending-invite rows for someone WITHOUT an account, whose `_id`
 *              stays the invite subdocument id
 *
 * Absent `revoked` - an older server, or a response that failed to include it -
 * this returns false and the row simply stays until the next getCourseUsers,
 * which is the behaviour we had before.
 */
function wasRevoked(row, revoked) {
  if (!row || !revoked) {
    return false;
  }

  const { usernames = [], userIds = [], inviteIds = [] } = revoked;

  if (row.username && usernames.includes(row.username)) {
    return true;
  }

  const rowId = row._id ? String(row._id) : null;

  return !!rowId && (userIds.includes(rowId) || inviteIds.includes(rowId));
}

const initialState = {
  courses: [],
  loadingCourses: false,
  courseStats: {},
  selectedCourse: null,
  showCourseDashboard: false,
  courseDashboardFilters: {},
  selectedTopic: null,
  selectedStudent: null,
  selectedGroupStudent: null,
  selectedReading: null,
  selectedReadingGrades: null,
  selectedWritingPrompt: null,
  showResearchEditor: false,
  selectedResearchPrompt: null,
  selectedResearchSubmission: null,
  questionsByTopicId: {},
  readingsInWild: {},
  readingsInCourse: {},
  readingsInAll: {},
  showCourseStudents: false,
  courseStudentsPageFilter: {},
};

export default function classRooms(state = initialState, action) {
  switch (action.type) {
    case Actions.LOAD_COURSES: {
      const courses = JSON.parse(JSON.stringify(state.courses));
      const newCourses = JSON.parse(JSON.stringify(action.courses || []));

      const oldCoursesMap = new Map();
      courses.forEach((course) => oldCoursesMap.set(course.courseId, course));

      // Merge oldCourses into newCourses based on courseId
      const mergedCourses = newCourses.map((newCourse) => {
        const courseId = newCourse.courseId;
        const oldCourse = oldCoursesMap.get(courseId);

        if (oldCourse) {
          // If a matching course is found in oldCourses, merge and override properties
          return { ...oldCourse, ...newCourse };
        }

        // If no matching course is found, keep the new course as is
        return newCourse;
      });

      return {
        ...state,
        courses: mergedCourses,
        loadingCourses: false,
      };
    }
    case Actions.SET_LOADING_COURSES: {
      return {
        ...state,
        loadingCourses: true,
      };
    }
    case Actions.LOAD_COURSE: {
      const index = state.courses.findIndex(
        (i) => i.courseId === action.courseId,
      );
      return {
        ...state,
        courses: [
          ...state.courses.slice(0, index),
          {
            ...state.courses[index],
            ...action.course,
          },
          ...state.courses.slice(index + 1),
        ],
      };
    }
    case Actions.LOAD_COURSE_TOPICS: {
      const courses = JSON.parse(JSON.stringify(state.courses));
      const course = courses.find((i) => i.courseId === action.courseId);

      if (!course) {
        return state;
      }

      course.topics = action.data.topics;
      course.writingPromptsCount = action.data.writingPromptsCount;
      course.researchPromptsCount = action.data.researchPromptsCount;

      return {
        ...state,
        courses: courses,
      };
    }
    case Actions.SET_LOADING_COURSE_USERS: {
      const index = state.courses.findIndex(
        (i) => i.courseId === action.courseId,
      );
      return {
        ...state,
        courses: [
          ...state.courses.slice(0, index),
          {
            ...state.courses[index],
            loadingCourseUsers: action.isLoading,
          },
          ...state.courses.slice(index + 1),
        ],
      };
    }
    case Actions.LOAD_COURSE_USERS: {
      const index = state.courses.findIndex(
        (i) => i.courseId === action.courseId,
      );
      return {
        ...state,
        courses: [
          ...state.courses.slice(0, index),
          {
            ...state.courses[index],
            ...action.courseUsers,
          },
          ...state.courses.slice(index + 1),
        ],
      };
    }
    case Actions.LOAD_TOPIC: {
      const index = state.courses.findIndex(
        (i) => i.courseId === action.courseId,
      );
      const topicIndex = state.courses[index].topics.findIndex(
        (i) => i._id === action.topicId,
      );
      state.courses[index].topics[topicIndex] = {
        ...state.courses[index].topics[topicIndex],
        ...action.topic,
      };
      return {
        ...state,
        courses: [...state.courses],
      };
    }
    case Actions.ADD_COURSES: {
      const course = { ...action.course };

      if (Array.isArray(course.readingOptions)) {
        course.readingsInWild = course.readingOptions.some((option) =>
          ['readings_in_wild', 'readings_in_wild_course_topics'].includes(
            option,
          ),
        );
      }

      return {
        ...state,
        courses: [...state.courses, course],
      };
    }
    case Actions.UPDATE_COURSES: {
      const courses = JSON.parse(JSON.stringify(state.courses));
      const updatedCourseId = action.course.courseId || action.course._id;
      const course = courses.find(
        (c) => c.courseId === updatedCourseId || c._id === updatedCourseId,
      );

      if (!course) {
        return state;
      }
      Object.assign(course, action.course);

      if (action.course.startDate === undefined) {
        course.startDate = course.startDate || '';
      }

      if (action.course.endDate === undefined) {
        course.endDate = course.endDate || '';
      }

      if (action.course.isFavorite === undefined) {
        course.isFavorite = course.isFavorite || false;
      }

      if (action.course.courseSettings === undefined) {
        course.courseSettings = course.courseSettings || false;
      }

      if (action.course.sourcesNote === undefined) {
        course.sourcesNote = course.sourcesNote || '';
      }

      if (Array.isArray(course.readingOptions)) {
        course.readingsInWild = course.readingOptions.some((option) =>
          ['readings_in_wild', 'readings_in_wild_course_topics'].includes(
            option,
          ),
        );
      }

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_DEFAULT_CLASSROOM_GROUP: {
      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === action.courseId);

      if (!course) {
        return state;
      }

      course.defaultClassroomGroup = action.classroomGroup.visibilitySettings;

      return {
        ...state,
        courses,
      };
    }
    case Actions.ADD_TOPIC: {
      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === action.courseId);

      course.topics = [
        ...course.topics.map((topic) => ({
          ...topic,
          new: false,
        })),
        {
          ...action.topic,
          new: true,
        },
      ];

      return {
        ...state,
        courses,
      };
    }
    case Actions.REMOVE_NEW_TAG_FROM_TOPIC: {
      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === action.courseId);

      if (course) {
        const topic = (course.topics || []).find(
          (t) => t._id === action.topicId,
        );

        if (topic) {
          topic.new = false;
        }
      }

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_TOPIC: {
      const courses = JSON.parse(JSON.stringify(state.courses));
      const updatedTopic = action.topic;

      const course = courses.find((c) => c.courseId === updatedTopic.courseId);

      course.topics = course.topics.map((topic) =>
        topic._id !== action.topicId
          ? topic
          : {
              ...topic,
              ...updatedTopic,
            },
      );

      return {
        ...state,
        courses,
      };
    }
    case Actions.ACCEPT_COURSE: {
      return {
        ...state,
        courses: state.courses.map((course) => ({
          ...course,
          invitedAsStudent:
            course.courseId !== action.courseId
              ? course.invitedAsStudent
              : false,
          invitedAsTeacher:
            course.courseId !== action.courseId
              ? course.invitedAsTeacher
              : false,
        })),
      };
    }
    case Actions.REMOVE_INVITED_COURSE: {
      return {
        ...state,
        courses: state.courses.filter((c) => c.courseId !== action.courseId),
      };
    }
    case Actions.CANCEL_INVITED_COURSE: {
      const courseId = action.courseId;
      const email = action.email;
      const isTeacher = action.isTeacher;
      const inviteUserType = isTeacher ? 'invitedTeachers' : 'invitedStudents';
      const courses = JSON.parse(JSON.stringify(state.courses));

      const newCourses = courses.map((c) => ({
        ...c,
        [inviteUserType]:
          c.courseId !== courseId
            ? c[inviteUserType]
            : c[inviteUserType].filter((c) => c.email !== email),
      }));

      return {
        ...state,
        courses: newCourses,
      };
    }
    case Actions.REMOVE_COURSE_USER: {
      const courseId = action.courseId;
      const username = action.username;
      const isTeacher = action.isTeacher;

      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === courseId);

      if (course) {
        if (isTeacher) {
          course.teachers = course.teachers.filter(
            (t) => t.username !== username,
          );
        } else {
          course.students = course.students.filter(
            (s) => s.username !== username,
          );
        }
      }

      return {
        ...state,
        courses,
      };
    }
    case Actions.ADD_INVITED_STUDENTS_COURSE: {
      const courseId = action.courseId;
      const emails = action.emails;
      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === courseId);

      course.invitedStudents = [...(course.invitedStudents || []), ...emails];
      course.invitedTeachers = (course.invitedTeachers || []).filter(
        (teacher) => !wasRevoked(teacher, action.revoked),
      );
      course.teachers = (course.teachers || []).filter(
        (teacher) => !wasRevoked(teacher, action.revoked),
      );

      return {
        ...state,
        courses,
      };
    }
    case Actions.ADD_INVITED_TEACHERS_COURSE: {
      const courseId = action.courseId;
      const emails = action.emails;
      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === courseId);

      course.invitedTeachers = [...(course.invitedTeachers || []), ...emails];
      course.invitedStudents = (course.invitedStudents || []).filter(
        (s) => !wasRevoked(s, action.revoked),
      );
      course.students = (course.students || []).filter(
        (student) => !wasRevoked(student, action.revoked),
      );

      return {
        ...state,
        courses,
      };
    }
    case Actions.LOAD_TOPIC_GOALS: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const data = action.data;
      const courses = JSON.parse(JSON.stringify(state.courses));

      const newCourses = courses.map((c) => ({
        ...c,
        topics:
          c.courseId !== courseId
            ? c.topics
            : c.topics.map((t) => ({
                ...t,
                goalsProgress:
                  t._id !== topicId ? t.goalsProgress : data.goalsProgress,
                readingStatistics:
                  t._id !== topicId
                    ? t.readingStatistics
                    : data.readingStatistics,
              })),
      }));

      return {
        ...state,
        courses: newCourses,
      };
    }
    case Actions.LOAD_ALL_STUDENT_PROGRESS: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const data = action.data;
      const students = data.students;
      const goalsCompletedByUsers = data.goalsCompletedByUsers;
      const writingGoalsCompletedByUsers = data.writingGoalsCompletedByUsers;
      const invitedStudents = data.invitedStudents;
      const courses = JSON.parse(JSON.stringify(state.courses));

      const newCourses = courses.map((c) => ({
        ...c,
        topics:
          c.courseId !== courseId
            ? c.topics
            : c.topics.map((t) => ({
                ...t,
                goalsProgress:
                  t._id !== topicId ? t.goalsProgress : goalsCompletedByUsers,
                writingGoalsCompletedByUsers:
                  t._id !== topicId
                    ? t.writingGoalsCompletedByUsers
                    : writingGoalsCompletedByUsers,
                students: t._id !== topicId ? t.students : students,
              })),
        invitedStudents:
          c.courseId !== courseId ? c.invitedStudents : invitedStudents,
      }));

      return {
        ...state,
        courses: newCourses,
      };
    }
    case Actions.SET_LOADING_TOPIC_GOALS: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const courses = JSON.parse(JSON.stringify(state.courses));

      const newCourses = courses.map((c) => ({
        ...c,
        topics:
          c.courseId !== courseId
            ? c.topics
            : c.topics.map((t) => ({
                ...t,
                loadingGoals:
                  t._id !== topicId ? t.loadingGoals : action.isLoading,
              })),
      }));

      return {
        ...state,
        courses: newCourses,
      };
    }
    case Actions.UPDATE_TOPIC_GOALS: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const goals = action.goals || [];
      const courses = JSON.parse(JSON.stringify(state.courses));

      const newCourses = courses.map((c) => ({
        ...c,
        topics:
          c.courseId !== courseId
            ? c.topics
            : c.topics.map((t) => ({
                ...t,
                goals: t._id !== topicId ? t.goals : goals,
              })),
      }));

      return {
        ...state,
        courses: newCourses,
      };
    }
    case Actions.UPDATE_READING_GOALS: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const readingId = action.readingId;
      const goals = action.goals.map((g) => ({
        _id: g.goal,
        goal: g.goalAs,
        target: g.value,
      }));
      const courses = JSON.parse(JSON.stringify(state.courses));

      const newCourses = courses.map((c) => ({
        ...c,
        topics:
          c.courseId !== courseId
            ? c.topics
            : c.topics.map((t) => ({
                ...t,
                readingStatistics:
                  t._id !== topicId
                    ? t.readingStatistics
                    : t.readingStatistics.map((r) => ({
                        ...r,
                        goals: r._id !== readingId ? r.goals : goals,
                      })),
              })),
      }));

      return {
        ...state,
        courses: newCourses,
      };
    }
    case Actions.LOAD_QUIZ_QUESTIONS: {
      return {
        ...state,
        questionsByTopicId: {
          ...(state.questionsByTopicId || {}),
          ...action.questions,
        },
      };
    }
    case Actions.UPDATE_READING_QUESTIONS: {
      const clonedQuestions = _.cloneDeep(state.questionsByTopicId) || {};

      const updatedQuestions = action.questions || [];
      const updatedQuestionIds = updatedQuestions.map((q) => q._id);
      const deletedQuestions = action.deletedQuestions || [];

      const questionsByTopic = clonedQuestions[action.topicId] || [];

      const otherQuestions = questionsByTopic.filter(
        (q) =>
          !updatedQuestionIds.includes(q._id) &&
          !deletedQuestions.includes(q._id),
      );

      clonedQuestions[action.topicId] = otherQuestions.concat(updatedQuestions);

      return {
        ...state,
        questionsByTopicId: clonedQuestions,
      };
    }
    case Actions.ADD_READING: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const reading = action.reading;

      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === courseId);
      const topic = course.topics.find((t) => t._id === topicId);
      const readingStatistics = topic.readingStatistics.find(
        (r) => r._id === reading._id,
      );

      if (!readingStatistics) {
        topic.readingStatistics = [...topic.readingStatistics, reading];
      } else {
        topic.readingStatistics = topic.readingStatistics.map((r) => {
          if (r._id !== reading._id) {
            return r;
          }

          return {
            ...r,
            ...reading,
            isUploadingPdf: false,
          };
        });
      }

      return {
        ...state,
        courses,
      };
    }
    case Actions.CLEAR_ADDED_READINGS: {
      const courseId = action.courseId;
      const topicId = action.topicId;

      const newCourses = JSON.parse(JSON.stringify(state.courses));

      const course = newCourses.find((c) => c.courseId === courseId);
      const topic = course.topics.find((t) => t._id === topicId);

      topic.readingStatistics = (topic.readingStatistics || [])
        .filter(
          (r) =>
            !r.isUploadingPdf &&
            (!r.new || ((r.loading || r.success) && !r.error)),
        )
        .map((r) => {
          let updates = {};

          if (r.new && !r.loading) {
            updates.new = false;
          }

          return {
            ...r,
            ...updates,
          };
        });

      return {
        ...state,
        courses: newCourses,
      };
    }
    case Actions.REMOVE_READING: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const readingId = action.readingId;

      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === courseId);
      const topic = course.topics.find((t) => t._id === topicId);
      topic.readingStatistics = topic.readingStatistics.filter(
        (r) => r._id !== readingId,
      );

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_READING: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const reading = action.reading;

      const courses = JSON.parse(JSON.stringify(state.courses));

      const newCourses = courses.map((c) => ({
        ...c,
        topics:
          c.courseId !== courseId
            ? c.topics
            : c.topics.map((t) => ({
                ...t,
                readingStatistics:
                  t._id !== topicId
                    ? t.readingStatistics
                    : t.readingStatistics
                        .filter((r) => r._id !== reading._id)
                        .concat([reading]),
              })),
      }));

      return {
        ...state,
        courses: newCourses,
      };
    }
    case Actions.ADD_PENDING_SOURCE_INFO_READING: {
      const courseId = action.data.courseId;
      const topicId = action.data.topicId;
      const readingId = action.data.readingId;
      const pointId = action.data.pointId;
      const pdfUrl = action.data.pdfUrl;
      const url = action.data.url;
      const isReference = action.data.isReference;
      const readingFormat = action.data.readingFormat;
      const isUploadingPdf = action.data.isUploadingPdf;
      const isProcessing = action.data.isProcessing;
      const sourceTitle = action.data.sourceTitle;
      const sourceAuthor = action.data.sourceAuthor;
      const sourceDate = action.data.sourceDate;
      const sourceName = action.data.sourceName;
      const pdfFileName = action.data.pdfFileName;
      const errorMessage = action.data.errorMessage;
      const isUpdating = action.data.isUpdating;

      const courses = JSON.parse(JSON.stringify(state.courses));
      const course = courses.find((c) => c.courseId === courseId);

      if (course) {
        const topic = course.topics.find((t) => t._id === topicId);

        if (topic) {
          if (!Array.isArray(topic.pendingSourceInfoReadings)) {
            topic.pendingSourceInfoReadings = [];
          }

          topic.pendingSourceInfoReadings.push({
            readingId,
            courseId,
            topicId,
            pointId,
            pdfUrl,
            url,
            isReference,
            readingFormat,
            sourceTitle,
            sourceAuthor,
            sourceDate,
            sourceName,
            isUploadingPdf:
              typeof isUploadingPdf === 'boolean' ? isUploadingPdf : true,
            isProcessing:
              typeof isProcessing === 'boolean' ? isProcessing : true,
            isUpdating: isUpdating || false,
            pdfFileName,
            errorMessage,
          });
        }
      }

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_PENDING_SOURCE_INFO_READING: {
      const readingId = action.readingId;
      const courseId = action.courseId;
      const topicId = action.topicId;
      const sourceInfo = action.response;
      const hasError = action.error;

      const courses = JSON.parse(JSON.stringify(state.courses));
      const course = courses.find((c) => c.courseId === courseId);

      if (course) {
        const topic = course.topics.find((t) => t._id === topicId);

        if (topic) {
          if (!Array.isArray(topic.pendingSourceInfoReadings)) {
            topic.pendingSourceInfoReadings = [];
          }

          if (hasError) {
            topic.pendingSourceInfoReadings =
              topic.pendingSourceInfoReadings.filter(
                (r) => r.readingId !== readingId,
              );
          } else if (sourceInfo) {
            topic.pendingSourceInfoReadings =
              topic.pendingSourceInfoReadings.map((r) => {
                if (r.readingId !== readingId) {
                  return r;
                }

                return {
                  ...r,
                  sourceTitle: sourceInfo.sourceTitle,
                  sourceName: sourceInfo.sourceName,
                  sourceFormat: sourceInfo.sourceFormat,
                  sourceDate: sourceInfo.sourceDate,
                  sourceAuthor: sourceInfo.sourceAuthor,
                  s3PdfFilename: sourceInfo.s3PdfFilename,
                  isUploadingPdf: false,
                  isProcessing: false,
                };
              });
          }
        }
      }

      return {
        ...state,
        courses,
      };
    }
    case Actions.REMOVE_PENDING_SOURCE_INFO_READING: {
      const readingId = action.readingId;
      const courseId = action.courseId;
      const topicId = action.topicId;

      const courses = JSON.parse(JSON.stringify(state.courses));
      const course = courses.find((c) => c.courseId === courseId);

      if (course) {
        const topic = course.topics.find((t) => t._id === topicId);

        if (topic) {
          if (!Array.isArray(topic.pendingSourceInfoReadings)) {
            topic.pendingSourceInfoReadings = [];
          }

          topic.pendingSourceInfoReadings =
            topic.pendingSourceInfoReadings.filter(
              (r) => r.readingId !== readingId,
            );
        }
      }

      return {
        ...state,
        courses,
      };
    }
    case Actions.REMOVE_COURSE: {
      const courseId = action.courseId;

      let courses = JSON.parse(JSON.stringify(state.courses));

      courses = courses.filter((c) => c.courseId !== courseId);

      return {
        ...state,
        courses,
      };
    }
    case Actions.REMOVE_TOPIC: {
      const courseId = action.courseId;
      const topicId = action.topicId;

      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === courseId);
      course.topics = course.topics.filter((t) => t._id !== topicId);

      return {
        ...state,
        courses,
      };
    }
    case Actions.ADD_STUDENT_STATS: {
      const courseId = action.courseId;
      const stats = action.stats;

      const courseStats = JSON.parse(JSON.stringify(state.courseStats));

      courseStats[courseId] = {
        ...(courseStats[courseId] || {}),
        ...stats,
      };

      return {
        ...state,
        courseStats,
      };
    }
    case Actions.UPDATE_SOURCE_FORMAT_READING: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const pointId = action.pointId;
      const sourceFormat = action.sourceFormat;

      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === courseId);
      const topic = course.topics.find((t) => t._id === topicId);
      const reading = topic.readingStatistics.find(
        (r) => r.pointId === pointId,
      );

      reading.sourceFormat = sourceFormat;

      return {
        ...state,
        courses,
      };
    }
    case Actions.SELECT_CLASSROOM_ENTITIES: {
      return {
        ...state,
        ...action.entities,
      };
    }
    case Actions.SELECT_COURSE: {
      return {
        ...state,
        selectedCourse: action.courseId,
        showCourseDashboard: false,
      };
    }
    case Actions.SELECT_COURSE_TAB: {
      return {
        ...state,
        selectedCourseTab: action.courseTab,
      };
    }
    case Actions.SELECT_COURSE_FAQ_ID: {
      return {
        ...state,
        selectedCourseFAQId: action.courseFAQId,
      };
    }
    case Actions.SELECT_SHOW_COURSE_DASHBOARD: {
      return {
        ...state,
        showCourseDashboard: action.showCourseDashboard,
        courseDashboardFilters: action.courseDashboardFilters,
        courseDashboardReportName: action.courseDashboardReportName || null,
      };
    }
    case Actions.REMOVE_COURSE_FILTERS: {
      return {
        ...state,
        courseDashboardFilters: {},
      };
    }
    case Actions.SET_COURSE_DASHBOARD_REPORT_NAME:
      return {
        ...state,
        courseDashboardReportName: action.courseDashboardReportName,
      };
    case Actions.SELECT_SHOW_COURSE_TEACHERS:
      return {
        ...state,
        showCourseTeachers: action.showCourseTeachers,
      };
    case Actions.SELECT_SHOW_COURSE_STUDENTS:
      return {
        ...state,
        showCourseStudents: action.showCourseStudents,
        courseStudentsPageFilter: action.filter || {},
      };
    case Actions.SELECT_SHOW_COURSE_SCHOOLS:
      return {
        ...state,
        showCourseSchools: action.showCourseSchools,
      };
    case Actions.SELECT_SHOW_COURSE_LOCATIONS:
      return {
        ...state,
        showCourseLocations: action.showCourseLocations,
      };
    case Actions.SELECT_SHOW_COURSE_TOP_STUDENTS:
      return {
        ...state,
        showCourseTopStudents: action.showCourseTopStudents,
      };
    case Actions.SELECT_TOPIC: {
      return {
        ...state,
        selectedTopic: action.topicId,
      };
    }
    case Actions.SELECT_TOPIC_TAB: {
      return {
        ...state,
        selectedTopicTab: action.topicTab,
      };
    }
    case Actions.SELECT_STUDENT: {
      return {
        ...state,
        selectedStudent: action.studentId,
      };
    }
    case Actions.SELECT_GROUP_STUDENT: {
      return {
        ...state,
        selectedGroupStudent: action.groupStudentId,
      };
    }
    case Actions.SELECT_READING: {
      return {
        ...state,
        selectedReading: action.readingId,
      };
    }
    case Actions.SELECT_READING_GRADES: {
      return {
        ...state,
        selectedReadingGrades: action.readingId,
      };
    }
    case Actions.SELECT_READING_GRADES_USERS: {
      return {
        ...state,
        selectedReadingGradesUsers: action.users,
      };
    }
    case Actions.SELECT_WRITING_PROMPT: {
      return {
        ...state,
        selectedWritingPrompt: action.writingPromptId,
        selectedResearchPrompt: null,
        selectedResearchSubmission: null,
        showResearchEditor: false,
      };
    }
    case Actions.SELECT_RESEARCH_PROMPT: {
      return {
        ...state,
        selectedResearchPrompt: action.researchPromptId,
      };
    }
    case Actions.SELECT_RESEARCH_SUBMISSION: {
      return {
        ...state,
        showResearchEditor: action.researchPromptId ? true : false,
        selectedResearchPrompt: action.researchPromptId,
        selectedResearchSubmission: action.researchSubmissionId,
      };
    }
    case Actions.RESET_COURSE: {
      return JSON.parse(JSON.stringify(initialState));
    }
    case Actions.UPDATE_WRITING_PROMPT_SETTING: {
      const courses = JSON.parse(JSON.stringify(state.courses));
      const updatedTopic = action.topic;

      const course = courses.find((c) => c.courseId === updatedTopic.courseId);

      course.topics = course.topics.map((topic) =>
        topic._id !== action.topicId
          ? topic
          : {
              ...topic,
              ...updatedTopic,
            },
      );

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_RESEARCH_PROMPT_SETTING: {
      const courses = JSON.parse(JSON.stringify(state.courses));
      const updatedTopic = action.topic;

      const course = courses.find((c) => c.courseId === updatedTopic.courseId);

      course.topics = course.topics.map((topic) =>
        topic._id !== action.topicId
          ? topic
          : {
              ...topic,
              ...updatedTopic,
            },
      );

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_READING_IN_WILD_SETTING: {
      let courses = JSON.parse(JSON.stringify(state.courses));
      const courseId = action.courseId;

      const course = courses.find((course) => course.courseId === courseId);
      course.readingsInWild = action.readingsInWild;

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_VOCABULARY_SETTING: {
      let courses = JSON.parse(JSON.stringify(state.courses));
      const courseId = action.courseId;

      const course = courses.find((course) => course.courseId === courseId);
      course.vocabularyEnabled = action.vocabularyEnabled;

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_COURSE_READING_IN_WILD: {
      const readingsInWild = JSON.parse(
        JSON.stringify(state.readingsInWild || {}),
      );
      const courseId = action.courseId;
      const readingsByStudents = action.data;

      readingsInWild[courseId] = readingsByStudents;

      return {
        ...state,
        readingsInWild,
      };
    }
    case Actions.UPDATE_COURSE_READING_IN_COURSE: {
      const readingsInCourse = JSON.parse(
        JSON.stringify(state.readingsInCourse || {}),
      );
      const courseId = action.courseId;
      const readingsByStudents = action.data;

      readingsInCourse[courseId] = readingsByStudents;

      return {
        ...state,
        readingsInCourse,
      };
    }
    case Actions.UPDATE_COURSE_READING_IN_ALL: {
      const readingsInAll = JSON.parse(
        JSON.stringify(state.readingsInAll || {}),
      );
      const courseId = action.courseId;
      const readingsByStudents = action.data;

      readingsInAll[courseId] = readingsByStudents;

      return {
        ...state,
        readingsInAll,
      };
    }
    case Actions.LOAD_TOPIC_READINGS: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const courseIndex = state.courses.findIndex(
        (i) => i.courseId === courseId,
      );
      if (courseIndex === -1) return state;
      const topicIndex = state.courses[courseIndex].topics.findIndex(
        (i) => i._id === topicId,
      );
      if (topicIndex === -1) return state;
      const existingTopic = state.courses[courseIndex].topics[topicIndex];
      // getTopicReadings doesn't compute per-student stats, so its
      // readingStatistics is only a placeholder. Once the real,
      // per-student readingStatistics has been loaded (e.g. by
      // getTopicGoals), keep it rather than clobbering it here.
      const readingStatistics =
        existingTopic.readingStatistics !== undefined
          ? existingTopic.readingStatistics
          : action.topic.readingStatistics;

      return {
        ...state,
        courses: [
          ...state.courses.slice(0, courseIndex),
          {
            ...state.courses[courseIndex],
            topics: [
              ...state.courses[courseIndex].topics.slice(0, topicIndex),
              {
                ...existingTopic,
                ...action.topic,
                readingStatistics,
              },
              ...state.courses[courseIndex].topics.slice(topicIndex + 1),
            ],
          },
          ...state.courses.slice(courseIndex + 1),
        ],
      };
    }
    case Actions.LOAD_TOPIC_WRITING_PROMPTS: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const courseIndex = state.courses.findIndex(
        (i) => i.courseId === courseId,
      );
      if (courseIndex === -1) return state;
      const topicIndex = state.courses[courseIndex].topics.findIndex(
        (i) => i._id === topicId,
      );
      if (topicIndex === -1) return state;

      return {
        ...state,
        courses: [
          ...state.courses.slice(0, courseIndex),
          {
            ...state.courses[courseIndex],
            topics: [
              ...state.courses[courseIndex].topics.slice(0, topicIndex),
              {
                ...state.courses[courseIndex].topics[topicIndex],
                ...action.topic,
              },
              ...state.courses[courseIndex].topics.slice(topicIndex + 1),
            ],
          },
          ...state.courses.slice(courseIndex + 1),
        ],
      };
    }
    case Actions.LOAD_TOPIC_RESEARCH_PROMPTS: {
      const courseId = action.courseId;
      const topicId = action.topicId;
      const courseIndex = state.courses.findIndex(
        (i) => i.courseId === courseId,
      );
      if (courseIndex === -1) return state;
      const topicIndex = state.courses[courseIndex].topics.findIndex(
        (i) => i._id === topicId,
      );
      if (topicIndex === -1) return state;

      return {
        ...state,
        courses: [
          ...state.courses.slice(0, courseIndex),
          {
            ...state.courses[courseIndex],
            topics: [
              ...state.courses[courseIndex].topics.slice(0, topicIndex),
              {
                ...state.courses[courseIndex].topics[topicIndex],
                ...action.topic,
              },
              ...state.courses[courseIndex].topics.slice(topicIndex + 1),
            ],
          },
          ...state.courses.slice(courseIndex + 1),
        ],
      };
    }
    case Actions.UPDATE_PROPAGATE_STATUS: {
      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === action.courseId);

      if (!course) {
        return state;
      }

      course.notPropagatedChanges = action.notPropagatedChanges;

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_MASTER_COURSE: {
      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === action.courseId);

      if (!course) {
        return state;
      }

      course.masterCourseId = action.masterCourseId;
      course.masterCourse = action.masterCourse;

      return {
        ...state,
        courses,
      };
    }
    case Actions.MARK_COURSE_AS_MASTER_COURSE: {
      const courses = JSON.parse(JSON.stringify(state.courses));

      const course = courses.find((c) => c.courseId === action.courseId);

      if (!course) {
        return state;
      }

      course.isMasterCourse = true;

      return {
        ...state,
        courses,
      };
    }
    case Actions.UPDATE_DEFAULT_COURSE_TAB: {
      const course = state.courses.find((i) => i.courseId === action.courseId);

      if (!course) {
        return state;
      }

      course.userDefaultTab = action.defaultTab;
      return {
        ...state,
        courses: [...state.courses],
      };
    }
    default:
      return state;
  }
}
